import React, { useState, useEffect, useMemo } from 'react';
import { app } from '../firebase'; // Importez votre configuration Firebase
import { getFirestore, collection, onSnapshot, query, where, orderBy, updateDoc, doc, setDoc, addDoc, Timestamp, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions'; 
import Loader from '../components/Loader';
import { isRentalManager, useUser } from '../contexts/UserContext';
import { datetimeFromTimestampToString } from '../librairies/Dates';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import PrimaryButton from '../components/buttons/PrimaryButton';
import Input from '../components/inputs/Input';
import TextareaInput from '../components/inputs/TextareaInput';
import SelectInput from '../components/inputs/SelectInput';
import CheckboxInput from '../components/inputs/CheckboxInput';
import Label from '../components/texts/Label';
import { rentalManagerCanMakeActionOnCoownership } from '../librairies/UsersLib';
import { downloadCsvFile } from '../librairies/CsvLib';
import { getResponseStartTs, getSlotCapacity, sortSurveyResponsesByStartTs } from './appointmentSlots';

const db = getFirestore(app);
const functions = getFunctions(app, 'europe-west3');
ChartJS.register(ArcElement, Tooltip, Legend);

const formatAnswerUserStatus = (userStatus) => {
  if (userStatus === "proprietaire") return "Copropriétaire";
  if (userStatus === "gestionnaireLocatif") return "Gestionnaire locatif";
  return "Locataire";
};

const DetailsSurvey = ({survey}) => {

  const [loader, setLoader] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [surveyData, setSurveyData] = useState(survey?.data ? survey.data() : null);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [deletingAnswer, setDeletingAnswer] = useState(null);
  const [editedSelectedAnswers, setEditedSelectedAnswers] = useState([]);
  const [editedAddressDetail, setEditedAddressDetail] = useState("");
  const [editReason, setEditReason] = useState("");
  const [isSavingSurveyAnswer, setIsSavingSurveyAnswer] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeletingSurveyAnswer, setIsDeletingSurveyAnswer] = useState(false);
  const [isRemindingNonRespondents, setIsRemindingNonRespondents] = useState(false);
  const [remindFeedback, setRemindFeedback] = useState("");
  const [showAddAnswerModal, setShowAddAnswerModal] = useState(false);
  const [addAnswerMode, setAddAnswerMode] = useState("existing");
  const [eligibleMembers, setEligibleMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [addSelectedAnswers, setAddSelectedAnswers] = useState([]);
  const [newAnswerFirstname, setNewAnswerFirstname] = useState("");
  const [newAnswerLastname, setNewAnswerLastname] = useState("");
  const [newAnswerPhone, setNewAnswerPhone] = useState("");
  const [newAnswerEmail, setNewAnswerEmail] = useState("");
  const [addAnswerAddressDetail, setAddAnswerAddressDetail] = useState("");
  const [newAnswerUserStatus, setNewAnswerUserStatus] = useState("locataire");
  const [isSavingNewAnswer, setIsSavingNewAnswer] = useState(false);
  const [addAnswerError, setAddAnswerError] = useState("");
  const [showOwnAnswerModal, setShowOwnAnswerModal] = useState(false);
  const [ownSelectedAnswers, setOwnSelectedAnswers] = useState([]);
  const [ownAddressDetail, setOwnAddressDetail] = useState("");
  const [ownAnswerError, setOwnAnswerError] = useState("");
  const [isSavingOwnAnswer, setIsSavingOwnAnswer] = useState(false);

  //contexte de l'utilisateur connecté
  const user = useUser();
  const canManageSurveyAnswers = rentalManagerCanMakeActionOnCoownership(user.user);

  useEffect(() => {
    const collectionRef = collection(db, user.user.userData.lastCoownershipLoaded.path+"/surveys/"+survey.id+"/answers");

    // Ajouter la condition where
    const q = query(collectionRef, where("status", "==", "accepted"), orderBy("dateSave", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {

      if(snapshot.docs.length > 0){
        
        let arr = [];

        snapshot.docs.map((answer) => {
          arr.push(answer);
        });
        
        setAnswers(arr);
      }else{
        setAnswers([]);
      }

      setLoader(false);
    });

    return () => {
      console.log("unsubscribe answers");
      unsubscribe();
    }
  }, []); 

  useEffect(() => {
    const surveyRef = doc(db, user.user.userData.lastCoownershipLoaded.path+"/surveys/"+survey.id);
    const unsubscribeSurvey = onSnapshot(surveyRef, (snapshot) => {
      if (snapshot.exists()) {
        setSurveyData(snapshot.data());
      }
    });

    return () => {
      unsubscribeSurvey();
    };
  }, []);

  const currentUserId = user.user?.user?.uid || "";
  const surveyIsFinished = surveyData?.dateEnd?.seconds != null
    && Math.floor(Date.now() / 1000) > surveyData.dateEnd.seconds;
  const canAnswerSurveyAsRentalManager = isRentalManager(user.user)
    && surveyData?.coownerTarget === true
    && !surveyIsFinished;
  const complementaryInfoText = surveyData?.complementaryInfo != null
    ? String(surveyData.complementaryInfo).trim()
    : "";

  const isOwnSurveyAnswer = (answerDoc) => {
    if (!currentUserId || !answerDoc) return false;
    if (answerDoc.id === currentUserId) return true;
    const userIdPath = answerDoc.data()?.userId?.path;
    return answerDoc.data()?.userStatus === "gestionnaireLocatif"
      && Boolean(userIdPath)
      && userIdPath.split("/").pop() === currentUserId;
  };

  const ownSurveyAnswer = answers.find((answerDoc) => isOwnSurveyAnswer(answerDoc)) || null;
  const showAnswerActionsColumn = canManageSurveyAnswers || Boolean(ownSurveyAnswer && canAnswerSurveyAsRentalManager);

  const generateColors = (count) => {
    const predefinedColors = [
      'silver', 'navy', 'maroon', 'olive', 'coral',
      'turquoise', 'salmon', 'orchid', 'plum', 'lavender', 'tan', 'aqua', 'chocolate', 'khaki', 'peru', 
      'blue', 'red', 'yellow', 'orange', 'green', 'purple', 'cyan', 'magenta', 'lime', 'pink',
      'teal', 'brown', 'indigo', 'violet', 'gold'
    ];
  
    return Array.from({ length: count }, (_, i) => predefinedColors[i % predefinedColors.length]);
  };

  const compareAnswersByStartTs = (a, b) => {
    const aTs = getResponseStartTs(a.data());
    const bTs = getResponseStartTs(b.data());
    if (aTs != null && bTs != null) return aTs - bTs;
    if (aTs != null) return -1;
    if (bTs != null) return 1;
    return 0;
  };

  const orderedSurveyResponses = useMemo(() => {
    const responses = surveyData?.responses || [];
    if (surveyData?.type !== "creneau") return responses;
    return sortSurveyResponsesByStartTs(responses);
  }, [surveyData]);

  const sortedAnswersForDisplay = useMemo(() => {
    if (surveyData?.type !== "creneau") return answers;
    return [...answers].sort(compareAnswersByStartTs);
  }, [answers, surveyData?.type]);

  // Le pie chart repose sur les réponses "accepted" chargées dans `answers`
  const labels = orderedSurveyResponses.map((response) => response.text);
  const answerCountByLabel = answers.reduce((acc, answerDoc) => {
    const selectedAnswers = answerDoc.data().answer || [];
    selectedAnswers.forEach((selectedAnswer) => {
      const key = String(selectedAnswer);
      acc[key] = (acc[key] || 0) + 1;
    });
    return acc;
  }, {});
  const data = labels.map((label) => answerCountByLabel[label] || 0);

  // Génération des couleurs dynamiques
  const backgroundColors = generateColors(labels.length); // Ajout d'opacité
  const borderColors = generateColors(labels.length);

  const dataChartPie = {

    labels: labels,
    datasets: [
      {
        label: '# Nombre de réponses',
        data: data,
        backgroundColor: backgroundColors, 
        borderColor: borderColors,
        borderWidth: 1,
      },
    ],
  }

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((acc, data) => acc + data, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} réponse${value>1?"s" : ""} (${percentage}%)`;
          }
        }
      }
    }
  };

  const downloadCSV = () => {
    const headers = ["Prenom Nom", "Telephone", "Numero de logement", "Reponse"];

    const sorted = [...answers].sort(compareAnswersByStartTs);
    const rows = sorted.map((answer) => {
      const answerData = answer.data();
      return [
        `${answerData.firstname || ""} ${answerData.lastname || ""}`.trim(),
        answerData.phone || "",
        answerData.addressDetail || "",
        (answerData.answer || []).join(", "),
      ];
    });

    downloadCsvFile({
      headers,
      rows,
      filename: `sondage-creneau-${survey.id}.csv`,
    });
  };

  const openEditSurveyAnswerModal = (answer) => {
    const canEditOwnAnswer = canAnswerSurveyAsRentalManager && isOwnSurveyAnswer(answer);
    if (!canManageSurveyAnswers && !canEditOwnAnswer) return;
    setEditingAnswer(answer);
    setEditedSelectedAnswers(answer.data().answer || []);
    setEditedAddressDetail(answer.data().addressDetail || "");
    setEditReason("");
  };

  const closeEditSurveyAnswerModal = () => {
    if (isSavingSurveyAnswer) return;
    setEditingAnswer(null);
    setEditedSelectedAnswers([]);
    setEditedAddressDetail("");
    setEditReason("");
  };

  const toggleEditedSelection = (responseText) => {
    const surveyType = survey.data().type;

    if (surveyType === "simple" || surveyType === "creneau") {
      setEditedSelectedAnswers([responseText]);
      return;
    }

    setEditedSelectedAnswers((prev) => (
      prev.includes(responseText)
        ? prev.filter((item) => item !== responseText)
        : [...prev, responseText]
    ));
  };

  const isEditedSelected = (responseText) => editedSelectedAnswers.includes(responseText);

  const countAcceptedAnswersForSlot = (responseText, excludeAnswerId = null) => {
    return answers.filter((answerDoc) => {
      if (excludeAnswerId && answerDoc.id === excludeAnswerId) return false;
      return (answerDoc.data().answer || []).includes(responseText);
    }).length;
  };

  const getCreneauCapacity = (responseText) => {
    const response = (surveyData?.responses || []).find((item) => item.text === responseText);
    return getSlotCapacity(response);
  };

  const isCreneauUnavailable = (responseText) => {
    if (surveyData?.type !== "creneau") return false;
    return countAcceptedAnswersForSlot(responseText, editingAnswer?.id) >= getCreneauCapacity(responseText);
  };

  const isCreneauUnavailableForAdd = (responseText) => {
    if (surveyData?.type !== "creneau") return false;
    return countAcceptedAnswersForSlot(responseText) >= getCreneauCapacity(responseText);
  };

  const memberHasAcceptedAnswer = (memberId) => {
    if (!memberId) return false;
    return answers.some((answerDoc) => {
      if (answerDoc.id === memberId) return true;
      const userIdPath = answerDoc.data()?.userId?.path;
      return userIdPath && userIdPath.split("/").pop() === memberId;
    });
  };

  const selectedMember = eligibleMembers.find((member) => member.id === selectedMemberId);
  const selectedMemberAlreadyAnswered = addAnswerMode === "existing" && memberHasAcceptedAnswer(selectedMemberId);

  const buildEligibleMembers = async () => {
    const coownershipPath = user.user.userData.lastCoownershipLoaded.path;
    const members = [];

    if (surveyData?.tenantTarget) {
      const tenantSnapshot = await getDocs(collection(db, `${coownershipPath}/tenant`));
      tenantSnapshot.docs.forEach((tenantDoc) => {
        const data = tenantDoc.data();
        members.push({
          id: tenantDoc.id,
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          email: data.email || "",
          phone: data.phone || "",
          userStatus: "locataire",
          coownershipComitee: false,
        });
      });
    }

    if (surveyData?.coownerTarget) {
      const ownerSnapshot = await getDocs(collection(db, `${coownershipPath}/owner`));
      ownerSnapshot.docs.forEach((ownerDoc) => {
        const data = ownerDoc.data();
        members.push({
          id: ownerDoc.id,
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          email: data.email || "",
          phone: data.phone || "",
          userStatus: "proprietaire",
          coownershipComitee: data.coownershipComitee || false,
        });
      });
    } else if (surveyData?.coownershipComiteeTarget) {
      const ownerSnapshot = await getDocs(collection(db, `${coownershipPath}/owner`));
      ownerSnapshot.docs.forEach((ownerDoc) => {
        const data = ownerDoc.data();
        if (!data.coownershipComitee) return;
        members.push({
          id: ownerDoc.id,
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          email: data.email || "",
          phone: data.phone || "",
          userStatus: "proprietaire",
          coownershipComitee: true,
        });
      });
    }

    return members.sort((a, b) => {
      const statusOrder = (member) => (member.userStatus === "proprietaire" ? 0 : 1);
      const statusDiff = statusOrder(a) - statusOrder(b);
      if (statusDiff !== 0) return statusDiff;
      return `${a.lastname} ${a.firstname}`.localeCompare(
        `${b.lastname} ${b.firstname}`,
        "fr",
        { sensitivity: "base" }
      );
    });
  };

  const openAddSurveyAnswerModal = async () => {
    if (!canManageSurveyAnswers) return;
    setAddAnswerError("");
    setAddAnswerMode("existing");
    setSelectedMemberId("");
    setAddSelectedAnswers([]);
    setNewAnswerFirstname("");
    setNewAnswerLastname("");
    setNewAnswerPhone("");
    setNewAnswerEmail("");
    setAddAnswerAddressDetail("");
    setNewAnswerUserStatus("locataire");
    setShowAddAnswerModal(true);

    const members = await buildEligibleMembers();
    setEligibleMembers(members);
  };

  const closeAddSurveyAnswerModal = () => {
    if (isSavingNewAnswer) return;
    setShowAddAnswerModal(false);
    setAddAnswerError("");
    setIsSavingNewAnswer(false);
  };

  const toggleAddSelection = (responseText) => {
    const surveyType = surveyData?.type || survey.data().type;

    if (surveyType === "simple" || surveyType === "creneau") {
      setAddSelectedAnswers([responseText]);
      return;
    }

    setAddSelectedAnswers((prev) =>
      prev.includes(responseText)
        ? prev.filter((item) => item !== responseText)
        : [...prev, responseText]
    );
  };

  const isAddSelected = (responseText) => addSelectedAnswers.includes(responseText);

  const formatSurveyAnswerOptionLabel = (response, excludeAnswerId = null) => {
    if (surveyData?.type === "creneau") {
      const count = countAcceptedAnswersForSlot(response.text, excludeAnswerId);
      const capacity = getSlotCapacity(response);
      return `${response.text} (${count}/${capacity})`;
    }
    const nbAnswers = response.nbAnswers ?? 0;
    return `${response.text} (${nbAnswers} vote${nbAnswers > 1 ? "s" : ""})`;
  };

  const renderSurveyAnswerChoices = ({
    name,
    maxHeightClass,
    isSelected,
    isDisabled,
    onToggle,
    excludeAnswerId = null,
  }) => {
    const isMultiple = surveyData?.type === "multiple";

    return (
      <div>
        <Label text="Réponse(s)" required={true} />
        <div className={`mb-4 ${maxHeightClass} overflow-auto rounded-lg border border-gray-300 p-2`}>
          {orderedSurveyResponses.map((response, index) => {
            const optionLabel = formatSurveyAnswerOptionLabel(response, excludeAnswerId);
            const disabled = isDisabled(response.text);
            const selected = isSelected(response.text);

            if (isMultiple) {
              return (
                <CheckboxInput
                  key={index}
                  id={`${name}-${index}`}
                  name={name}
                  label={optionLabel}
                  checked={selected}
                  disabled={disabled}
                  onChange={() => onToggle(response.text)}
                />
              );
            }

            return (
              <label
                key={index}
                className={`mb-2 flex cursor-pointer items-center gap-2 rounded p-2 ${
                  selected ? "bg-blue-50" : "bg-transparent"
                } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
              >
                <input
                  type="radio"
                  name={name}
                  checked={selected}
                  disabled={disabled}
                  onChange={() => onToggle(response.text)}
                  className="h-4 w-4 border-gray-300 text-primary"
                />
                <span className="text-sm text-gray-700">{optionLabel}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const validateAddSurveyAnswer = async () => {
    if (isSavingNewAnswer || selectedMemberAlreadyAnswered) return;

    const formattedAnswers = addSelectedAnswers
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);

    if (formattedAnswers.length === 0) {
      setAddAnswerError("Vous devez sélectionner au moins une réponse.");
      return;
    }

    if (surveyData?.type === "creneau" && isCreneauUnavailableForAdd(formattedAnswers[0])) {
      setAddAnswerError("Ce créneau n'a plus de place disponible.");
      return;
    }

    let answerPayload = null;

    if (addAnswerMode === "existing") {
      if (!selectedMemberId) {
        setAddAnswerError("Veuillez sélectionner une personne.");
        return;
      }
      if (!selectedMember) {
        setAddAnswerError("Personne introuvable.");
        return;
      }
      answerPayload = {
        userId: doc(db, `users/${selectedMember.id}`),
        source: "web",
        answer: formattedAnswers,
        dateSave: Timestamp.now(),
        status: "accepted",
        phone: selectedMember.phone || "",
        firstname: selectedMember.firstname || "",
        lastname: selectedMember.lastname || "",
        userStatus: selectedMember.userStatus,
        coownershipComitee: selectedMember.coownershipComitee || false,
        addressDetail: addAnswerAddressDetail.trim(),
      };
    } else {
      if (!newAnswerFirstname.trim() || !newAnswerLastname.trim()) {
        setAddAnswerError("Le prénom et le nom sont requis.");
        return;
      }
      answerPayload = {
        userId: doc(db, `users/${user.user.user.uid}`),
        accountNotExisting: true,
        source: "web",
        answer: formattedAnswers,
        dateSave: Timestamp.now(),
        status: "accepted",
        phone: newAnswerPhone.trim(),
        firstname: newAnswerFirstname.trim(),
        lastname: newAnswerLastname.trim(),
        email: newAnswerEmail.trim(),
        userStatus: newAnswerUserStatus,
        coownershipComitee: false,
        addressDetail: addAnswerAddressDetail.trim(),
      };
    }

    setIsSavingNewAnswer(true);
    setAddAnswerError("");

    try {
      const answersCollectionRef = collection(
        db,
        `${user.user.userData.lastCoownershipLoaded.path}/surveys/${survey.id}/answers`
      );

      if (addAnswerMode === "existing") {
        await setDoc(doc(answersCollectionRef, selectedMemberId), answerPayload);
      } else {
        await addDoc(answersCollectionRef, answerPayload);
      }

      closeAddSurveyAnswerModal();
    } catch (error) {
      console.error("Erreur lors de l'ajout de la reponse :", error);
      setAddAnswerError("Erreur lors de l'ajout de la réponse.");
    } finally {
      setIsSavingNewAnswer(false);
    }
  };

  const openOwnAnswerModal = () => {
    if (!canAnswerSurveyAsRentalManager || ownSurveyAnswer) return;
    setOwnAnswerError("");
    setOwnSelectedAnswers([]);
    setOwnAddressDetail("");
    setShowOwnAnswerModal(true);
  };

  const closeOwnAnswerModal = () => {
    if (isSavingOwnAnswer) return;
    setShowOwnAnswerModal(false);
    setOwnAnswerError("");
    setIsSavingOwnAnswer(false);
  };

  const toggleOwnSelection = (responseText) => {
    const surveyType = surveyData?.type || survey.data().type;

    if (surveyType === "simple" || surveyType === "creneau") {
      setOwnSelectedAnswers([responseText]);
      return;
    }

    setOwnSelectedAnswers((prev) =>
      prev.includes(responseText)
        ? prev.filter((item) => item !== responseText)
        : [...prev, responseText]
    );
  };

  const validateOwnSurveyAnswer = async () => {
    if (!canAnswerSurveyAsRentalManager || ownSurveyAnswer || isSavingOwnAnswer || !currentUserId) return;

    const formattedAnswers = ownSelectedAnswers
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);

    if (formattedAnswers.length === 0) {
      setOwnAnswerError("Vous devez sélectionner au moins une réponse.");
      return;
    }

    if (surveyData?.type === "creneau" && isCreneauUnavailableForAdd(formattedAnswers[0])) {
      setOwnAnswerError("Ce créneau n'a plus de place disponible.");
      return;
    }

    const profile = user.user.userData || {};
    const answerPayload = {
      userId: doc(db, `users/${currentUserId}`),
      source: "web",
      answer: formattedAnswers,
      dateSave: Timestamp.now(),
      status: "accepted",
      phone: profile.phone || "",
      firstname: profile.firstname || "",
      lastname: profile.lastname || "",
      email: profile.email || "",
      userStatus: "gestionnaireLocatif",
      coownershipComitee: false,
      addressDetail: ownAddressDetail.trim(),
      reasonModification: "",
      reasonDeletion: "",
    };

    setIsSavingOwnAnswer(true);
    setOwnAnswerError("");

    try {
      await setDoc(
        doc(db, `${user.user.userData.lastCoownershipLoaded.path}/surveys/${survey.id}/answers/${currentUserId}`),
        answerPayload
      );
      closeOwnAnswerModal();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la réponse :", error);
      setOwnAnswerError("Erreur lors de l'enregistrement de la réponse.");
    } finally {
      setIsSavingOwnAnswer(false);
    }
  };

  const validateEditSurveyAnswer = async () => {
    if (!editingAnswer || isSavingSurveyAnswer) return;

    const formattedAnswers = editedSelectedAnswers
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);

    if (formattedAnswers.length === 0) {
      return;
    }

    if (surveyData?.type === "creneau" && isCreneauUnavailable(formattedAnswers[0])) {
      return;
    }

    setIsSavingSurveyAnswer(true);
    try {
      await updateDoc(editingAnswer.ref, {
        answer: formattedAnswers,
        source: "web",
        addressDetail: editedAddressDetail.trim(),
        reasonModification: editReason.trim(),
      });
      closeEditSurveyAnswerModal();
    } catch (error) {
      console.error("Erreur lors de la mise a jour de la reponse :", error);
    } finally {
      setIsSavingSurveyAnswer(false);
    }
  };

  const openDeleteSurveyAnswerModal = (answer) => {
    if (!canManageSurveyAnswers) return;
    setDeletingAnswer(answer);
    setDeleteReason("");
  };

  const closeDeleteSurveyAnswerModal = () => {
    if (isDeletingSurveyAnswer) return;
    setDeletingAnswer(null);
    setDeleteReason("");
  };

  const remindNonRespondents = async () => {
    if (!canManageSurveyAnswers || isRemindingNonRespondents) return;

    const confirmed = window.confirm(
      "Relancer les personnes qui n'ont pas encore répondu à ce sondage ? Elles recevront une notification et un e-mail."
    );
    if (!confirmed) return;

    setRemindFeedback("");
    setIsRemindingNonRespondents(true);

    try {
      const coownershipId = user.user.userData.lastCoownershipLoaded.path.split("/")[1];
      const remindSurveyNonRespondents = httpsCallable(functions, "remindSurveyNonRespondents");
      const result = await remindSurveyNonRespondents({
        coownershipId,
        surveyId: survey.id,
      });

      setRemindFeedback(result.data?.message || "Relance envoyée.");
    } catch (error) {
      console.error("Erreur lors de la relance des non-répondants :", error);
      setRemindFeedback(error?.message || error?.code || "Erreur lors de la relance.");
    } finally {
      setIsRemindingNonRespondents(false);
    }
  };

  const validateDeleteSurveyAnswer = async () => {
    if (!deletingAnswer || isDeletingSurveyAnswer) return;

    setIsDeletingSurveyAnswer(true);
    try {
      await updateDoc(deletingAnswer.ref, {
        status: "deleted",
        source: "web",
        reasonDeletion: deleteReason.trim(),
        reasonModification: ""
      });
      closeDeleteSurveyAnswerModal();
    } catch (error) {
      console.error("Erreur lors de la suppression du vote :", error);
    } finally {
      setIsDeletingSurveyAnswer(false);
    }
  };

  return (
    <div>
      {loader ? (
        <Loader />
      ) : (
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          {(surveyData?.type === "creneau" || canManageSurveyAnswers || canAnswerSurveyAsRentalManager) && (
            <div className='flex flex-wrap gap-4 justify-end p-2'>
              {canAnswerSurveyAsRentalManager && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (ownSurveyAnswer) {
                      openEditSurveyAnswerModal(ownSurveyAnswer);
                      return;
                    }
                    openOwnAnswerModal();
                  }}
                  className="justify-end flex mb-4"
                >
                  <PrimaryButton
                    type={"button"}
                    value={ownSurveyAnswer ? "Modifier ma réponse" : "Répondre"}
                    loading={false}
                  />
                </a>
              )}
              {surveyData?.type === "creneau" && (
                <a href="#" onClick={(e) => { e.preventDefault(); downloadCSV(); }} className="justify-end flex mb-4">
                  <PrimaryButton type={"button"} value={"Exporter en csv"} loading={false} />
                </a>
              )}
              {canManageSurveyAnswers && (
                <>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openAddSurveyAnswerModal();
                    }}
                    className="justify-end flex mb-4"
                  >
                    <PrimaryButton type={"button"} value={"Ajouter une réponse"} loading={false} />
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      remindNonRespondents();
                    }}
                    className="justify-end flex mb-4"
                  >
                    <PrimaryButton
                      type={"button"}
                      value={"Relancer les non-répondants"}
                      loading={isRemindingNonRespondents}
                      disabled={isRemindingNonRespondents}
                    />
                  </a>
                </>
              )}
            </div>
          )}
          {remindFeedback !== "" && (
            <p className={`mx-2 mb-2 text-center text-sm ${remindFeedback.includes("Erreur") ? "text-red-500" : "text-green-600"}`}>
              {remindFeedback}
            </p>
          )}
          <p className='m-2 font-bold text-center'>Question: {surveyData?.title}</p>
          
          {surveyData?.type !== "creneau" && (
            <div className='my-4 text-center w-60 h-60 lg:w-96 lg:h-96 m-auto'>
              <Pie data={dataChartPie} options={options}/>
            </div>
          )}
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  Prénom Nom
                </th>
                <th scope="col" className="px-6 py-3">
                  Statut
                </th>
                <th scope="col" className="px-6 py-3">
                  Téléphone
                </th>
                <th scope="col" className="px-6 py-3">
                  Numéro de logement
                </th>
                <th scope="col" className="px-6 py-3">
                  Réponse
                </th>
                <th scope="col" className="px-6 py-3">
                  Date
                </th>
                {showAnswerActionsColumn && (
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedAnswersForDisplay.map((answer, key) => {
                return (
                  <tr key={key} className={key % 2 === 0 ? "bg-white border-b dark:bg-gray-900 dark:border-gray-700" : "border-b bg-gray-50 dark:bg-gray-800 dark:border-gray-700"}>
                    <td className="px-6 py-4">
                      {answer.data().firstname + " " + answer.data().lastname}
                    </td>
                    <td className="px-6 py-4">
                      {formatAnswerUserStatus(answer.data().userStatus)}
                    </td>
                    <td className="px-6 py-4">
                      {!rentalManagerCanMakeActionOnCoownership(user.user) && answer.data().userStatus === "proprietaire" ? (
                        ""
                      ) : answer.data().phone}
                    </td>
                    <td className="px-6 py-4">
                      {answer.data().addressDetail}
                    </td>
                    <td className="px-6 py-4">
                      {answer.data().answer.map((element) => element).join(', ')}
                    </td>
                    <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                      {datetimeFromTimestampToString(answer.data().dateSave.seconds)}
                    </th>
                    {showAnswerActionsColumn && (
                      <td className="px-6 py-4">
                        {(canManageSurveyAnswers || (canAnswerSurveyAsRentalManager && isOwnSurveyAnswer(answer))) && (
                          <div className="flex items-center gap-3">
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openEditSurveyAnswerModal(answer);
                              }}
                              className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                            >
                              Modifier
                            </a>
                            {canManageSurveyAnswers && (
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openDeleteSurveyAnswerModal(answer);
                                }}
                                className="font-medium text-red-600 hover:underline"
                              >
                                Supprimer
                              </a>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingAnswer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <p className="mb-4 text-lg font-semibold text-gray-900">Modifier la réponse</p>
            {complementaryInfoText && isOwnSurveyAnswer(editingAnswer) && (
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="mb-1 text-sm font-semibold text-gray-900">Information complémentaire</p>
                <p className="whitespace-pre-wrap text-sm text-gray-700">{complementaryInfoText}</p>
              </div>
            )}
            {renderSurveyAnswerChoices({
              name: "edited-survey-answer",
              maxHeightClass: "max-h-60",
              isSelected: isEditedSelected,
              isDisabled: isCreneauUnavailable,
              onToggle: toggleEditedSelection,
              excludeAnswerId: editingAnswer?.id,
            })}
            <Input
              id="edited-address-detail"
              name="editedAddressDetail"
              type="text"
              label="Numéro de logement"
              value={editedAddressDetail}
              onChange={(e) => setEditedAddressDetail(e.target.value)}
            />
            <TextareaInput
              id="edit-reason"
              name="editReason"
              label="Raison de modification"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              required={false}
            />
            <div className="mt-6 flex justify-end gap-3">
              <PrimaryButton
                type="button"
                value="Annuler"
                bgColor="bg-gray-500"
                onClick={closeEditSurveyAnswerModal}
                disabled={isSavingSurveyAnswer}
              />
              <PrimaryButton
                type="button"
                value={isSavingSurveyAnswer ? "Validation..." : "Valider"}
                loading={isSavingSurveyAnswer}
                onClick={validateEditSurveyAnswer}
                disabled={isSavingSurveyAnswer}
              />
            </div>
          </div>
        </div>
      )}

      {showAddAnswerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <p className="mb-4 text-lg font-semibold text-gray-900">Ajouter une réponse</p>

            <div className="mb-4 flex flex-wrap gap-2">
              <PrimaryButton
                type="button"
                value="Compte existant"
                bgColor={addAnswerMode === "existing" ? "bg-secondary" : "bg-gray-500"}
                onClick={() => {
                  setAddAnswerMode("existing");
                  setAddAnswerError("");
                }}
              />
              <PrimaryButton
                type="button"
                value="Nouvelle personne"
                bgColor={addAnswerMode === "manual" ? "bg-secondary" : "bg-gray-500"}
                onClick={() => {
                  setAddAnswerMode("manual");
                  setAddAnswerError("");
                  setSelectedMemberId("");
                }}
              />
            </div>

            {addAnswerMode === "existing" ? (
              <>
                <SelectInput
                  id="add-answer-member"
                  name="selectedMemberId"
                  label="Personne"
                  value={selectedMemberId}
                  onChange={(e) => {
                    setSelectedMemberId(e.target.value);
                    setAddAnswerError("");
                  }}
                  placeholder="Sélectionner une personne"
                >
                  {eligibleMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.firstname} {member.lastname} —{" "}
                      {formatAnswerUserStatus(member.userStatus)}
                    </option>
                  ))}
                </SelectInput>
                {selectedMemberAlreadyAnswered && (
                  <p className="mb-3 text-sm text-red-500">Cette personne a déjà répondu au sondage</p>
                )}
              </>
            ) : (
              <div className="mb-4 grid grid-cols-1 gap-1 md:grid-cols-2">
                <Input
                  id="new-answer-firstname"
                  name="newAnswerFirstname"
                  type="text"
                  label="Prénom"
                  value={newAnswerFirstname}
                  onChange={(e) => setNewAnswerFirstname(e.target.value)}
                />
                <Input
                  id="new-answer-lastname"
                  name="newAnswerLastname"
                  type="text"
                  label="Nom"
                  value={newAnswerLastname}
                  onChange={(e) => setNewAnswerLastname(e.target.value)}
                />
                <Input
                  id="new-answer-phone"
                  name="newAnswerPhone"
                  type="tel"
                  label="Téléphone"
                  value={newAnswerPhone}
                  onChange={(e) => setNewAnswerPhone(e.target.value)}
                />
                <Input
                  id="new-answer-email"
                  name="newAnswerEmail"
                  type="email"
                  label="Email"
                  value={newAnswerEmail}
                  onChange={(e) => setNewAnswerEmail(e.target.value)}
                />
                <SelectInput
                  id="new-answer-user-status"
                  name="newAnswerUserStatus"
                  label="Statut"
                  value={newAnswerUserStatus}
                  onChange={(e) => setNewAnswerUserStatus(e.target.value)}
                >
                  <option value="locataire">Locataire</option>
                  <option value="proprietaire">Copropriétaire</option>
                </SelectInput>
              </div>
            )}

            <Input
              id="add-answer-address-detail"
              name="addAnswerAddressDetail"
              type="text"
              label="Numéro de logement"
              value={addAnswerAddressDetail}
              onChange={(e) => setAddAnswerAddressDetail(e.target.value)}
            />

            {renderSurveyAnswerChoices({
              name: "add-survey-answer",
              maxHeightClass: "max-h-48",
              isSelected: isAddSelected,
              isDisabled: isCreneauUnavailableForAdd,
              onToggle: toggleAddSelection,
            })}

            {addAnswerError !== "" && (
              <p className="mb-3 text-sm text-red-500">{addAnswerError}</p>
            )}

            <div className="flex justify-end gap-3">
              <PrimaryButton
                type="button"
                value="Annuler"
                bgColor="bg-gray-500"
                onClick={closeAddSurveyAnswerModal}
                disabled={isSavingNewAnswer}
              />
              <PrimaryButton
                type="button"
                value={isSavingNewAnswer ? "Ajout..." : "Valider"}
                loading={isSavingNewAnswer}
                onClick={validateAddSurveyAnswer}
                disabled={isSavingNewAnswer || selectedMemberAlreadyAnswered}
              />
            </div>
          </div>
        </div>
      )}

      {showOwnAnswerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <p className="mb-4 text-lg font-semibold text-gray-900">Répondre au sondage</p>
            {complementaryInfoText && (
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="mb-1 text-sm font-semibold text-gray-900">Information complémentaire</p>
                <p className="whitespace-pre-wrap text-sm text-gray-700">{complementaryInfoText}</p>
              </div>
            )}
            <Input
              id="own-answer-address-detail"
              name="ownAddressDetail"
              type="text"
              label="Numéro de logement"
              value={ownAddressDetail}
              onChange={(e) => setOwnAddressDetail(e.target.value)}
              required={false}
            />
            {renderSurveyAnswerChoices({
              name: "own-survey-answer",
              maxHeightClass: "max-h-48",
              isSelected: (responseText) => ownSelectedAnswers.includes(responseText),
              isDisabled: isCreneauUnavailableForAdd,
              onToggle: toggleOwnSelection,
            })}
            {ownAnswerError !== "" && (
              <p className="mb-3 text-sm text-red-500">{ownAnswerError}</p>
            )}
            <div className="flex justify-end gap-3">
              <PrimaryButton
                type="button"
                value="Annuler"
                bgColor="bg-gray-500"
                onClick={closeOwnAnswerModal}
                disabled={isSavingOwnAnswer}
              />
              <PrimaryButton
                type="button"
                value={isSavingOwnAnswer ? "Validation..." : "Valider"}
                loading={isSavingOwnAnswer}
                onClick={validateOwnSurveyAnswer}
                disabled={isSavingOwnAnswer}
              />
            </div>
          </div>
        </div>
      )}

      {deletingAnswer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <p className="mb-4 text-lg font-semibold text-gray-900">Supprimer le vote</p>
            <TextareaInput
              id="delete-reason"
              name="deleteReason"
              label="Raison de suppression"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              required={false}
            />
            <div className="mt-6 flex justify-end gap-3">
              <PrimaryButton
                type="button"
                value="Annuler"
                bgColor="bg-gray-500"
                onClick={closeDeleteSurveyAnswerModal}
                disabled={isDeletingSurveyAnswer}
              />
              <PrimaryButton
                type="button"
                value={isDeletingSurveyAnswer ? "Suppression..." : "Confirmer"}
                bgColor="bg-red-600"
                loading={isDeletingSurveyAnswer}
                onClick={validateDeleteSurveyAnswer}
                disabled={isDeletingSurveyAnswer}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailsSurvey;
